/**
 * The Supabase OTP session of the email gate, stored under "aile.emailGate".
 * Its access token goes to the backend at wallet sign-in, which checks the
 * allowlist. Hiding UI here is UX; the backend's 403 is the security boundary.
 */

import { storageKeys, timing } from "../config";
import { api, type SupabaseSessionResponse } from "./api";
import { getEmailGate } from "./runtimeConfig";

/** The venue's allowlist answer for this email, as of the last check. */
export type EmailAccess = "approved" | "already" | "waitlisted";

export interface EmailSession {
  email: string;
  accessToken: string;
  refreshToken: string;
  /** Unix seconds when the access token expires. */
  expiresAt: number;
  /** Last known allowlist decision; absent when never checked. */
  access?: EmailAccess;
}

function readStored(): EmailSession | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(storageKeys.emailGate);
  } catch {
    return null; // storage unavailable (privacy mode) — gate re-runs per tab
  }
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<EmailSession>;
    if (
      typeof parsed.email === "string" &&
      typeof parsed.accessToken === "string" &&
      typeof parsed.refreshToken === "string" &&
      typeof parsed.expiresAt === "number"
    ) {
      return parsed as EmailSession;
    }
  } catch {
    // Corrupt entry — treated as absent.
  }
  return null;
}

function writeStored(session: EmailSession | null): void {
  try {
    if (session === null) localStorage.removeItem(storageKeys.emailGate);
    else localStorage.setItem(storageKeys.emailGate, JSON.stringify(session));
  } catch {
    // Storage unavailable — the in-tab flow still worked; nothing to persist.
  }
}

/** Folds Supabase's optional-everything session payload into a stored session. */
function toSession(raw: SupabaseSessionResponse, email: string): EmailSession | null {
  if (typeof raw.access_token !== "string" || typeof raw.refresh_token !== "string") return null;
  const expiresAt =
    typeof raw.expires_at === "number"
      ? raw.expires_at
      : Math.floor(Date.now() / 1000) + (typeof raw.expires_in === "number" ? raw.expires_in : 3600);
  const confirmed = typeof raw.user?.email === "string" ? raw.user.email : email;
  return {
    email: confirmed.toLowerCase(),
    accessToken: raw.access_token,
    refreshToken: raw.refresh_token,
    expiresAt,
  };
}

/** The email the user has verified in this browser, or null. */
export function getVerifiedEmail(): string | null {
  return readStored()?.email ?? null;
}

/**
 * Last known allowlist decision for the stored email, or null when there is
 * no session (or it was never checked). "approved" is what opens the wallet
 * step; anything else keeps the visitor on the waitlist screen.
 */
export function getStoredAccess(): EmailAccess | null {
  return readStored()?.access ?? null;
}

/**
 * Ask the venue's allowlist about the stored email (check_access RPC, called
 * with the user's own token) and remember the answer. Null when there is no
 * usable session or the RPC misbehaves — callers treat null as "cannot say",
 * not as a denial. UX only: the gateway re-runs this check at wallet login.
 */
export async function checkEmailAccess(): Promise<EmailAccess | null> {
  const coords = gateCoordinates();
  const token = await getSupabaseToken();
  if (coords === null || token === null) return null;
  let answer: string;
  try {
    answer = await api.emailAccessCheck(coords.supabaseUrl, coords.supabaseAnonKey, token);
  } catch {
    return null;
  }
  if (answer !== "approved" && answer !== "already" && answer !== "waitlisted") return null;
  const stored = readStored();
  if (stored !== null) writeStored({ ...stored, access: answer });
  return answer;
}

/** Drop the Supabase session (the "use a different email" action). */
export function clearEmailSession(): void {
  writeStored(null);
}

function gateCoordinates(): { supabaseUrl: string; supabaseAnonKey: string } | null {
  const gate = getEmailGate();
  return gate.supabaseUrl !== null && gate.supabaseAnonKey !== null
    ? { supabaseUrl: gate.supabaseUrl, supabaseAnonKey: gate.supabaseAnonKey }
    : null;
}

/**
 * Ask Supabase to mail a 6-digit code. Throws `ApiError` — 429 is the rate
 * limit, anything else is "could not send". Requires config to have landed.
 */
export async function sendEmailCode(email: string): Promise<void> {
  const coords = gateCoordinates();
  if (coords === null) throw new Error("email gate is not configured");
  await api.emailOtpSend(coords.supabaseUrl, coords.supabaseAnonKey, email.trim().toLowerCase());
}

/**
 * Exchange the mailed code for a Supabase session and persist it. Throws
 * `ApiError` (403 = wrong or expired code) or `Error` on a malformed payload.
 */
export async function verifyEmailCode(email: string, code: string): Promise<EmailSession> {
  const coords = gateCoordinates();
  if (coords === null) throw new Error("email gate is not configured");
  const raw = await api.emailOtpVerify(
    coords.supabaseUrl,
    coords.supabaseAnonKey,
    email.trim().toLowerCase(),
    code.trim(),
  );
  const session = toSession(raw, email);
  if (session === null) throw new Error("Supabase verify answered without a session");
  writeStored(session);
  return session;
}

/**
 * Current Supabase access token for the gateway bind, refreshing it first
 * when it is (nearly) expired. Returns null when there is no session, the
 * gate is unconfigured, or the refresh is refused (revoked/rotated token) —
 * callers treat null as "run the OTP flow again". Never throws: a Supabase
 * brownout at sign-in degrades to a session without an email stamp, which
 * the gateway reports via /auth/me and the UI surfaces.
 */
export async function getSupabaseToken(): Promise<string | null> {
  const stored = readStored();
  if (stored === null) return null;
  if (stored.expiresAt - timing.supabaseRefreshMarginSec > Date.now() / 1000) return stored.accessToken;

  const coords = gateCoordinates();
  if (coords === null) return null;
  try {
    const raw = await api.emailOtpRefresh(
      coords.supabaseUrl,
      coords.supabaseAnonKey,
      stored.refreshToken,
    );
    const session = toSession(raw, stored.email);
    if (session === null) {
      writeStored(null);
      return null;
    }
    writeStored(session);
    return session.accessToken;
  } catch {
    // A refused refresh means the session is gone; a network failure might be
    // transient, but the stored token is expired either way — re-verify.
    writeStored(null);
    return null;
  }
}
